// eslint-disable-next-line no-restricted-imports
import type {
  MathJsStatic,
  MathNode as MathJsNode,
  Matrix as MathJsMatrix,
} from 'mathjs'
// eslint-disable-next-line no-restricted-imports
import {
  absDependencies,
  addDependencies,
  ceilDependencies,
  cosDependencies,
  create,
  divideDependencies,
  evaluateDependencies,
  expDependencies,
  floorDependencies,
  indexDependencies,
  log10Dependencies,
  logDependencies,
  lusolveDependencies,
  matrixDependencies,
  maxDependencies,
  minDependencies,
  multiplyDependencies,
  parseDependencies,
  piDependencies,
  powDependencies,
  qrDependencies,
  rangeDependencies,
  roundDependencies,
  sinDependencies,
  sqrtDependencies,
  subsetDependencies,
  subtractDependencies,
  tanDependencies,
  transposeDependencies,
  unaryMinusDependencies,
  unitDependencies,
} from 'mathjs'

// Arithmetic is part of the contract: a derived value is a formula over pens,
// and without add/subtract/divide/pow every expression containing + - / ^ threw
// "Function divide missing in provided namespace" and silently produced no
// series. Only pure numeric operators are added — nothing that can reach the
// host (import, createUnit, evaluate-of-strings) stays out on purpose.
//
// The trigonometric, log10 and rounding functions are here to match the agent's
// server-side whitelist. When the two sets disagree, the agent writes a KPI its
// own evaluator accepts, the chart cannot evaluate it, and the pen draws as
// nothing at all — which is indistinguishable from a model that found no signal.
const dependencies = {
  absDependencies,
  addDependencies,
  ceilDependencies,
  cosDependencies,
  divideDependencies,
  evaluateDependencies,
  expDependencies,
  floorDependencies,
  log10Dependencies,
  logDependencies,
  sinDependencies,
  tanDependencies,
  powDependencies,
  subtractDependencies,
  unaryMinusDependencies,
  indexDependencies,
  lusolveDependencies,
  matrixDependencies,
  maxDependencies,
  minDependencies,
  multiplyDependencies,
  parseDependencies,
  piDependencies,
  qrDependencies,
  rangeDependencies,
  roundDependencies,
  sqrtDependencies,
  subsetDependencies,
  transposeDependencies,
  unitDependencies,
}

const math = create(dependencies)

export type ForecastMath = Pick<
  MathJsStatic,
  | 'abs'
  | 'add'
  | 'divide'
  | 'evaluate'
  | 'exp'
  | 'log'
  | 'pow'
  | 'subtract'
  | 'unaryMinus'
  | 'index'
  | 'lusolve'
  | 'matrix'
  | 'max'
  | 'min'
  | 'multiply'
  | 'parse'
  | 'pi'
  | 'qr'
  | 'range'
  | 'round'
  | 'sqrt'
  | 'subset'
  | 'transpose'
  | 'unit'
  | 'isFunctionNode'
  | 'isOperatorNode'
  | 'isParenthesisNode'
  | 'isSymbolNode'
>

export const forecastMath = math as ForecastMath

export type MathNode = MathJsNode
export type Matrix = MathJsMatrix
